import {UserGroup} from '@nursio-link/common/model';

import {DEV_GROUPS_STORAGE_KEY, getGroups} from '@/services/authService';
import {graphqlClient} from '@/services/graphqlClient';
import type {
  GetGlobalApprovalStatusQuery,
  GetMyUserInfoQuery,
} from '@/services/graphqlHooks';
import {
  useGetGlobalApprovalStatusQuery,
  useGetMyUserInfoQuery,
} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';

export type CompliantStatus = {
  isTermApproved: boolean;
  isPrivacyApproved: boolean;
  isOwnerTermApproved: boolean;
  isOwner: boolean;
};

const getOwnerCompliantStatus = async (): Promise<CompliantStatus> => {
  const existingData = queryClient.getQueryData<GetGlobalApprovalStatusQuery>(
    useGetGlobalApprovalStatusQuery.getKey()
  );

  const globalStatus = existingData?.getGlobalApprovalStatus;

  const shouldRefetch =
    !globalStatus?.globalTermsAndConditions ||
    !globalStatus?.globalPrivacyPolicy;

  const globalApprovalStatus =
    await queryClient.fetchQuery<GetGlobalApprovalStatusQuery>({
      queryKey: useGetGlobalApprovalStatusQuery.getKey(),
      queryFn: () =>
        graphqlClient.request(useGetGlobalApprovalStatusQuery.document),
      staleTime: shouldRefetch ? 0 : Infinity,
    });

  const status = globalApprovalStatus?.getGlobalApprovalStatus;

  return {
    isTermApproved: !!status?.globalTermsAndConditions,
    isPrivacyApproved: !!status?.globalPrivacyPolicy,
    isOwnerTermApproved: true,
    isOwner: true,
  };
};

const getUserCompliantStatus = async (): Promise<CompliantStatus> => {
  const existingData = queryClient.getQueryData<GetMyUserInfoQuery>(
    useGetMyUserInfoQuery.getKey()
  );

  const approvedConditions = existingData?.getMyUserInfo?.approvedConditions;

  const shouldRefetch =
    !approvedConditions?.termsAndConditions ||
    !approvedConditions?.globalPrivacyPolicy;

  const userInfo = await queryClient.fetchQuery<GetMyUserInfoQuery>({
    queryKey: useGetMyUserInfoQuery.getKey(),
    queryFn: () => graphqlClient.request(useGetMyUserInfoQuery.document),
    staleTime: shouldRefetch ? 0 : Infinity,
  });

  const approved = userInfo?.getMyUserInfo?.approvedConditions;

  return {
    isTermApproved: !!approved?.termsAndConditions,
    isPrivacyApproved: !!approved?.globalPrivacyPolicy,
    isOwnerTermApproved: !!approved?.globalTermsAndConditions,
    isOwner: false,
  };
};

const getSuperuserCompliantStatus = (): CompliantStatus => {
  return {
    isTermApproved: true,
    isPrivacyApproved: true,
    isOwnerTermApproved: true,
    isOwner: false,
  };
};

const getOfflineCompliantStatus = (): CompliantStatus => {
  return {
    isTermApproved: true,
    isPrivacyApproved: true,
    isOwnerTermApproved: true,
    isOwner: false,
  };
};

const getErrorCompliantStatus = (): CompliantStatus => {
  return {
    isTermApproved: false,
    isPrivacyApproved: false,
    isOwnerTermApproved: false,
    isOwner: false,
  };
};

export const isCurrentUserCompliant = async (): Promise<CompliantStatus> => {
  if (
    import.meta.env.DEV &&
    window.sessionStorage.getItem(DEV_GROUPS_STORAGE_KEY)
  ) {
    return getSuperuserCompliantStatus();
  }
  try {
    const groups = await getGroups();
    const isOffline = !navigator.onLine;
    const isSuperuser = groups.includes(UserGroup.Superusers);
    const isOwner = groups.includes(UserGroup.Owners);

    if (isSuperuser) return getSuperuserCompliantStatus();
    if (isOffline) return getOfflineCompliantStatus();
    if (isOwner) return await getOwnerCompliantStatus();
    return await getUserCompliantStatus();
  } catch (error) {
    console.error('Failed to check approval status:', error);
    return getErrorCompliantStatus();
  }
};
